import analytics from './analytics';
import { logger } from 'modules/logger';

type CacheRegistryItem = {
  lastAccessed: number;
  size: number;
};
type CacheRegistryData = {
  totalSize: number;
  items: {
    [key: string]: CacheRegistryItem;
  };
};

class CacheRegistry {
  cacheKey: string;
  // this is a reference to the cache registry data stored in localStorage
  // accessing this is faster than reading from localStorage
  totalSize = 0;

  constructor(cacheKey: string) {
    this.cacheKey = cacheKey;
    window.addEventListener('storage', this.#handleExternalUpdates, false);
  }

  // If a different tab updates the cache registry, we need to update our
  // reference to the totalSize to keep it in sync
  #handleExternalUpdates = (event: StorageEvent) => {
    if (event.key === this.cacheKey) {
      if (event.newValue == null) {
        this.totalSize = 0;
      } else {
        this.totalSize = JSON.parse(event.newValue).totalSize;
      }
    }
  };

  read(): CacheRegistryData {
    const rawData = localStorage.getItem(this.cacheKey);
    if (rawData != null) {
      return JSON.parse(rawData);
    }
    return {
      totalSize: 0,
      items: {},
    };
  }

  reset(): void {
    localStorage.removeItem(this.cacheKey);
    this.totalSize = 0;
  }

  touch(key: string, size: number): void {
    const data = this.read();
    const existingItem = data.items[key];
    data.items[key] = { lastAccessed: Date.now(), size };
    try {
      if (existingItem != null) {
        if (existingItem.size !== size) {
          data.totalSize = data.totalSize - existingItem.size + size;
        }
      } else {
        data.totalSize += size;
      }
      localStorage.setItem(this.cacheKey, JSON.stringify(data));
      this.totalSize = data.totalSize;
    } catch (e: any) {
      logger.error(`touch failed for ${this.cacheKey}`, e);
      if (analytics) {
        analytics.error(e?.name, {
          key: this.cacheKey,
        });
      }
    }
  }

  remove(key: string): void {
    this.batchRemove([key]);
  }

  batchRemove(keys: Array<string>): void {
    const data = this.read();
    let freedSize = 0;
    for (const key of keys) {
      if (!(key in data.items)) {
        logger.warn(`[LRUSWCache] key "${key}" not found in cache registry upon removal`);
        continue;
      }
      freedSize += data.items[key].size;
      data.totalSize -= data.items[key].size;
      delete data.items[key];
    }
    data.totalSize = Math.max(0, data.totalSize);
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(data));
      this.totalSize = data.totalSize;
      if (freedSize > 0) {
        logger.info(`[LRUSWCache] batchRemove: freed ${freedSize}B, totalSize: ${data.totalSize}B`);
      }
    } catch (e: any) {
      logger.error(`[LRUSWCache] failed to setItem for ${this.cacheKey}`);
      if (analytics) {
        analytics.error(e?.name, {
          key: this.cacheKey,
        });
      }
    }
  }
}

export class LRUSWCache {
  cacheKey: string;
  maxSize: number;
  cache: Cache | null = null;
  registry: CacheRegistry;
  evictionThreshold: number;
  #versionChanged = false;

  constructor({
    key,
    version,
    maxSize,
    evictionThreshold = 0,
  }: {
    key: string;
    version: string;
    maxSize: number;
    // the eviction threshold is a percentage of the maximum cache size (0.0 - 1.0)
    evictionThreshold?: number;
  }) {
    this.cacheKey = key;
    this.maxSize = maxSize;
    this.registry = new CacheRegistry(key);
    this.evictionThreshold = evictionThreshold;

    const previousCacheVersion = localStorage.getItem(`${key}-version`);
    if (previousCacheVersion != null) {
      // if the cache version has changed, clear the cache registry
      if (previousCacheVersion !== version) {
        localStorage.removeItem(key);
        this.registry.reset();
        this.#versionChanged = true;
      }
    }
    try {
      localStorage.setItem(`${key}-version`, String(version));
    } catch (e: any) {
      logger.error(`[LRUSWCache] failed to setItem for ${key}-version`);
      analytics.error(e?.name, {
        key: `${key}-version`,
      });
    }
  }

  async open(): Promise<LRUSWCache> {
    if (this.cache == null) {
      if (this.#versionChanged) {
        await caches.delete(this.cacheKey);
        this.#versionChanged = false;
      }
      const cache = await caches.open(this.cacheKey);
      this.cache = cache;
      await this.#validateMetadata();
    }
    return this;
  }

  // checks if the cache is full and if so evicts the least recently used item
  #evict = async (incomingSize = 0): Promise<void> => {
    const data = this.registry.read();
    if (data.totalSize + incomingSize <= this.maxSize) {
      // cache is not full
      return;
    }
    const sortedKeys = Object.keys(data.items).sort(
      (a, b) => data.items[a].lastAccessed - data.items[b].lastAccessed
    );

    const targetSize = this.maxSize * (1 - this.evictionThreshold);
    let sizeToFree = data.totalSize + incomingSize - targetSize;

    // collect oldest items until we've freed enough space
    const keysToRemove: Array<string> = [];
    for (const key of sortedKeys) {
      if (sizeToFree <= 0) {
        break;
      }
      keysToRemove.push(key);
      sizeToFree -= data.items[key].size;
    }

    if (keysToRemove.length > 0) {
      const freedSize = keysToRemove.reduce((sum, key) => sum + data.items[key].size, 0);
      const postEvictionSize = data.totalSize - freedSize;
      logger.info(
        `[LRUSWCache] eviction: ${keysToRemove.length} entries, totalSize ${data.totalSize}B -> ~${postEvictionSize}B`
      );

      // warn if we evicted everything but still over limit (indicates corrupted data or sizing issue)
      if (keysToRemove.length === sortedKeys.length && postEvictionSize > this.maxSize) {
        logger.warn(
          `[LRUSWCache] evicted all entries but still over maxSize: postEvictionSize ${postEvictionSize}B > maxSize ${this.maxSize}B`
        );
      }

      // delete from cache first, then update registry
      const cache = this.#guardUninitialized();
      const deleteResults = await Promise.allSettled(keysToRemove.map((key) => cache.delete(key)));

      // collect keys to remove from registry: deleted (true) or already gone (false)
      // only keep registry entries for deletions that rejected/errored
      const keysToUnregister = keysToRemove.filter(
        (_, index) => deleteResults[index].status === 'fulfilled'
      );

      if (keysToUnregister.length > 0) {
        this.registry.batchRemove(keysToUnregister);
      }

      // warn about rejected deletions (actual errors)
      const erroredKeys = keysToRemove.filter(
        (_, index) => deleteResults[index].status === 'rejected'
      );
      if (erroredKeys.length > 0) {
        logger.warn(
          `[LRUSWCache] eviction: failed to delete ${erroredKeys.length} entries from cache: ${erroredKeys.join(', ')}`
        );
      }
    }
  };

  #requestInfoToURLString = (request: RequestInfo | URL): string => {
    if (request instanceof Request) {
      return request.url;
    } else {
      return request.toString();
    }
  };

  async #validateMetadata() {
    if (this.cache != null) {
      const cache = this.cache;
      const data = this.registry.read();
      const metadataKeys = Object.keys(data.items);
      const cacheKeys = (await cache.keys()).map((key) => this.#requestInfoToURLString(key));

      // find mismatches
      const cacheKeysSet = new Set(cacheKeys);
      const metadataKeysSet = new Set(metadataKeys);

      const entriesInCacheButNotMetadata = cacheKeys.filter((ck) => !metadataKeysSet.has(ck));
      const entriesInMetadataButNotCache = metadataKeys.filter((mk) => !cacheKeysSet.has(mk));

      if (entriesInCacheButNotMetadata.length > 0 || entriesInMetadataButNotCache.length > 0) {
        // selective cleanup: recover or remove orphaned entries
        if (entriesInCacheButNotMetadata.length > 0) {
          // Rebuild metadata from surviving cache entries instead of deleting them.
          // These are typically valid responses where registry.touch() never ran
          // (e.g. unawaited cache.put() completed after navigation, or localStorage
          // setItem failed). Re-registering preserves prefetched data and avoids
          // forcing a re-fetch on the next page load.
          logger.warn(
            `[LRUSWCache] found ${entriesInCacheButNotMetadata.length} untracked cache entries, rebuilding metadata`
          );
          for (const key of entriesInCacheButNotMetadata) {
            const response = await cache.match(key);
            const contentLength = Number(response?.headers.get('content-length') ?? 0);
            // Entries put with an explicit sizeOverride (e.g. responses missing content-length) have
            // no header to recover from; fall back to measuring the cached body directly.
            const size =
              contentLength > 0
                ? contentLength
                : ((await response?.clone().arrayBuffer())?.byteLength ?? 0);
            this.registry.touch(key, size);
          }
        }

        if (entriesInMetadataButNotCache.length > 0) {
          logger.warn(
            `[LRUSWCache] found ${entriesInMetadataButNotCache.length} entries in metadata but not in cache, removing from metadata`
          );
          this.registry.batchRemove(entriesInMetadataButNotCache);
        }
      }
    }
  }

  #guardUninitialized = (): Cache => {
    if (this.cache == null) {
      throw new Error('Cache is not initialized, call open() first');
    }
    return this.cache;
  };

  /**
   * @param sizeOverride Explicit byte size to record, used instead of reading `content-length`
   *   off the response. Required for responses that may omit the header (e.g. NRRD downloads) —
   *   without it, such entries silently register as 0 bytes and are invisible to eviction.
   */
  async put(request: RequestInfo | URL, response: Response, sizeOverride?: number): Promise<void> {
    const cache = this.#guardUninitialized();

    const size = sizeOverride ?? (Number(response.headers.get('content-length') ?? 0) || 0);
    const key = this.#requestInfoToURLString(request);

    try {
      await this.#evict(size);
      await cache.put(request, response);
      this.registry.touch(key, size);
    } catch (e: any) {
      logger.error(
        `[LRUSWCache] unable to cache data, the available storage may be full: ${e?.message}`
      );
    }
  }

  async match(request: RequestInfo | URL): Promise<Response | undefined> {
    const cache = this.#guardUninitialized();
    const resource = await cache.match(request);
    return resource;
  }

  async delete(request: RequestInfo | URL): Promise<boolean> {
    const cache = this.#guardUninitialized();

    const deleted = await cache.delete(request);
    if (deleted) {
      this.registry.remove(this.#requestInfoToURLString(request));

      return true;
    } else {
      return false;
    }
  }

  async deleteByPrefix(prefix: string): Promise<void> {
    const cache = this.#guardUninitialized();
    const allKeys = await cache.keys();
    await Promise.all(
      allKeys
        .filter((key) => this.#requestInfoToURLString(key).includes(prefix))
        .map((key) => this.delete(key))
    );
  }

  /** Returns all cache keys as Request objects (passthrough to the native Cache.keys()). */
  async keys(): Promise<readonly Request[]> {
    const cache = this.#guardUninitialized();
    return cache.keys();
  }

  async purge(): Promise<void> {
    this.registry.reset();
    if (this.cache != null) {
      const cache = this.cache;
      const allKeys = await cache.keys();
      await Promise.all(
        allKeys.map((key) => {
          return cache.delete(key);
        })
      );
    }
  }
}
